import React, { useEffect } from "react";
import { Input } from "@rebass/forms";
import { useField } from "formik";
import { InputHelper, InputHelperOptionalParameters } from "./InputHelper";
import { Flex } from "rebass";
import { NumberBox } from "./NumberInput";
import styled from "styled-components";

export type SliderType = "left" | "centered";

export const SliderInput: React.FC<{
  name: string;
  min: number;
  max: number;
  step?: number;
  sliderType?: SliderType;
  decimalPlaces?: number;
  unit?: string;
  helperParameters?: InputHelperOptionalParameters;
  value?: number;
  onChange?: (number) => void;
}> = ({
  step,
  name,
  sliderType,
  min,
  max,
  decimalPlaces,
  unit,
  helperParameters = { label: name },
  value,
}) => {
  const validate = (value) => {
    if (value === "") {
      return "Select a value";
    }
  };

  const [field, meta, helpers] = useField({ name, validate });

  const val = parseFloat(meta.value);
  const setValue = (value: number) => {
    helpers.setValue(value);
  };

  useEffect(() => {
    if (value) helpers.setValue(value);
  }, [value]);

  return (
    <InputHelper meta={meta} helpers={helpers} {...helperParameters}>
      <Flex alignContent="center" alignItems="center" width="200%">
        <NumberBox
          value={val}
          setValue={setValue}
          step={step}
          min={min}
          max={max}
          decimalPlaces={decimalPlaces}
          unit={unit}
        />
        <div style={{ width: "20px" }} />
        <SliderBar
          value={val}
          setValue={setValue}
          step={step}
          sliderType={sliderType}
          min={min}
          max={max}
        />
      </Flex>
    </InputHelper>
  );
};

export const SliderBar: React.FC<{
  value: number;
  setValue: (number) => void;
  min: number;
  max: number;
  step?: number;
  width?: number;
  sliderType?: SliderType;
  onChange?: (number) => void;
}> = ({
  value,
  setValue,
  min,
  max,
  step = 0.1,
  width = 300,
  sliderType,
  onChange,
}) => {
  if (sliderType == undefined)
    sliderType = min + max === 0 ? "centered" : "left";

  const background = "#f3f3f3";
  const highlight = "#3F90B5";

  let startPosition = sliderType == "centered" ? 50 : 0;
  let endPosition = sliderType == "left" ? 100 : value >= 0 ? 100 : 0;
  let sliderPosition = ((value - min) * 100) / (max - min);

  if (value < 0) {
    [startPosition, sliderPosition] = [sliderPosition, startPosition];
  }

  const gradientNodes =
    sliderType == "left"
      ? `${highlight} 0%, ${highlight} ${sliderPosition}%, ${background} ${sliderPosition}%, ${background} 100%`
      : `${background} 0%, ${background} ${startPosition}%, ${highlight} ${startPosition}%, ${highlight} ${sliderPosition}%, ${background} ${sliderPosition}%, ${background} ${endPosition}%`;

  return (
    <StyledSlider
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        if (onChange) onChange(e.target.value);
      }}
      width={width}
      gradientNodes={gradientNodes}
    />
  );
};

export const StyledSlider = styled.input<{
  width: number;
  gradientNodes: string;
}>`
  maxwidth: ${(props) => props.width}px;
  display: inline;
  marginleft: 20px;
  background: linear-gradient(to right, ${(props) => props.gradientNodes});

  -webkit-appearance: none; /* Override default CSS styles */
  appearance: none;
  width: 100%; /* Full-width */
  height: 22px; /* Specified height */
  outline: none; /* Remove outline */
  opacity: 1; /* Set transparency (for mouse-over effects on hover) */
  -webkit-transition: 0.2s; /* 0.2 seconds transition on hover */
  transition: opacity 0.2s;

  &::-webkit-slider-thumb {
    -webkit-appearance: none; /* Override default look */
    appearance: none;
    width: 22px; /* Set a specific slider handle width */
    height: 22px; /* Slider handle height */
    background: #2a6c8a; /* Green background */
    cursor: pointer; /* Cursor on hover */
  }

  &::-moz-range-thumb {
    width: 22px; /* Set a specific slider handle width */
    height: 22px; /* Slider handle height */
    background: #2a6c8a; /* Green background */
    cursor: pointer; /* Cursor on hover */
  }
`;
